import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PRODUCTS_API_ADDRESS } from './apiConfig';
import { useCart } from './CartContext';
import { TERM_LABELS, TERM_SUFFIX, TERM_MONTHS, usd } from './priceFormat';
import './hierarchy.css';

async function fetchJson(url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  // The Function double-encodes the body as a JSON string, so a first parse may
  // yield a string that still needs parsing.
  const payload = await res.json();
  return typeof payload === 'string' ? JSON.parse(payload) : payload;
}

// Excerpt whose content lives at a remote URL: fetch the markup and render it.
// The content is first-party (served from our own Products storage).
// TODO: verify CORS once real ExcerptUrl data exists — the host serving the
// excerpt markup must send Access-Control-Allow-Origin for this fetch to work.
function RemoteExcerptCard({ url }) {
  // status: { loading, error, html }
  const [status, setStatus] = useState({ loading: true, error: null, html: '' });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setStatus({ loading: true, error: null, html: '' });
    fetch(url, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.text();
      })
      .then(html => {
        if (!cancelled) setStatus({ loading: false, error: null, html });
      })
      .catch(error => {
        if (cancelled || error.name === 'AbortError') return;
        setStatus({ loading: false, error: 'Could not load this content.', html: '' });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [url]);

  return (
    <article className="item-card">
      {status.loading ? (
        <p className="loading-text">Loading…</p>
      ) : status.error ? (
        <p className="error-text">{status.error}</p>
      ) : (
        <div className="excerpt-content" dangerouslySetInnerHTML={{ __html: status.html }} />
      )}
    </article>
  );
}

function ExcerptCard({ excerpt, reverse }) {
  const { Description, ImageUrl, ExcerptUrl } = excerpt;
  // A non-empty URL wins: render the content fetched from it.
  if (ExcerptUrl) return <RemoteExcerptCard url={ExcerptUrl} />;
  return (
    <article className={`item-card item-card-split${reverse ? ' item-card-split-reverse' : ''}`}>
      {ImageUrl ? (
        <div className="item-card-split-media">
          <img className="item-image-full" src={ImageUrl} alt="" loading="lazy" />
        </div>
      ) : null}
      <div className="item-card-split-body">
        {Description ? <p className="item-text">{Description}</p> : null}
      </div>
    </article>
  );
}

// Group the flat price list into one entry per term, seats sorted ascending.
function groupPricesByTerm(prices) {
  const byTerm = new Map();
  for (const price of prices) {
    if (!byTerm.has(price.Term)) byTerm.set(price.Term, []);
    byTerm.get(price.Term).push(price);
  }
  return [...byTerm.entries()]
    .map(([term, list]) => ({
      term,
      prices: [...list].sort((a, b) => a.SeatCount - b.SeatCount),
    }))
    .sort((a, b) => a.term - b.term);
}

// Discount of a price vs. the base (monthly single-seat) rate scaled to this
// selection's seats and billed months. Returns a whole percent, or 0 if none.
function discountPercent(price, basePrice) {
  if (!price || !basePrice) return 0;
  const months = TERM_MONTHS[price.Term];
  if (!months) return 0;
  const reference = basePrice * price.SeatCount * months;
  if (reference <= 0) return 0;
  return Math.max(0, Math.round((1 - price.PriceUSD / reference) * 100));
}

function PriceTermCard({ term, price, discount, product, seatCount, prices }) {
  const label = TERM_LABELS[term] || `Term ${term}`;
  const suffix = TERM_SUFFIX[term] || '';
  const { matches, setCartItem } = useCart();
  const inCart = price ? matches(product.Id, term, seatCount) : false;

  // Single-item cart: adding replaces whatever was there. The full price list
  // travels with the item so term/seats stay editable from the cart.
  const handleAdd = () => {
    if (!price) return;
    setCartItem({
      productId: product.Id,
      productName: product.Name,
      imageUrl: product.ImageUrl,
      term,
      seatCount,
      prices,
    });
  };

  return (
    <article className="item-card">
      <h3 className="item-title">{label}</h3>
      <p className="price-amount">
        {price ? (
          <>
            {usd.format(price.PriceUSD)}
            <span className="price-suffix"> {suffix}</span>
          </>
        ) : (
          '—'
        )}
      </p>
      {discount > 0 ? <p className="price-discount">Save {discount}%</p> : null}
      <button
        type="button"
        className="btn btn--filled add-to-cart-btn"
        onClick={handleAdd}
        disabled={!price || inCart}
      >
        {inCart ? 'In cart ✓' : 'Add to Cart'}
      </button>
    </article>
  );
}

function Pricing({ prices, product }) {
  const groups = groupPricesByTerm(prices);
  const seatCounts = [...new Set(prices.map(p => p.SeatCount))].sort((a, b) => a - b);
  const [seatCount, setSeatCount] = useState(seatCounts[0]);

  // Base rate: monthly (Term 1), single seat — discounts are measured against it.
  const basePrice = prices.find(p => p.Term === 1 && p.SeatCount === 1)?.PriceUSD;

  return (
    <section className="item-group">
      <h2 className="item-group-title">Pricing</h2>
      <div className="price-seats">
        <label htmlFor="seats">Seats</label>
        <select
          id="seats"
          className="input"
          value={seatCount}
          onChange={e => setSeatCount(Number(e.target.value))}
        >
          {seatCounts.map(sc => (
            <option key={sc} value={sc}>{sc}</option>
          ))}
        </select>
      </div>
      <div className="item-grid">
        {groups.map(group => {
          const price = group.prices.find(p => p.SeatCount === seatCount);
          return (
            <PriceTermCard
              key={group.term}
              term={group.term}
              price={price}
              discount={discountPercent(price, basePrice)}
              product={product}
              seatCount={seatCount}
              prices={prices}
            />
          );
        })}
      </div>
      <p className="note note--spaced">
        Whether billed monthly or annually, there's no commitment — cancel your
        subscription anytime.
      </p>
    </section>
  );
}

function ProductDetailPage() {
  const { productId } = useParams();
  // status: { loading, error, product }
  const [status, setStatus] = useState({ loading: true, error: null, product: null });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setStatus({ loading: true, error: null, product: null });
    const url =
      `${PRODUCTS_API_ADDRESS}?operation=get_product` +
      `&product=${encodeURIComponent(productId)}`;
    fetchJson(url, controller.signal)
      .then(product => {
        if (cancelled) return;
        setStatus({ loading: false, error: null, product });
      })
      .catch(error => {
        if (cancelled || error.name === 'AbortError') return;
        setStatus({ loading: false, error: 'Could not load this product. Please try again later.', product: null });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [productId]);

  const { loading, error, product } = status;
  const excerpts = product?.Excerpts || [];
  const prices = product?.Prices || [];

  return (
    <div className="page">
      <p>
        <Link className="text-link" to="/products">← Back to products</Link>
      </p>

      {loading ? (
        <p className="loading-text">Loading product…</p>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : !product ? (
        <p className="empty-text">Product not found.</p>
      ) : (
        <>
          <section className="item-group">
            <div className="item-stack">
              <div className="item-card item-card-split">
                {product.ImageUrl ? (
                  <div className="item-card-split-media">
                    <img className="item-image-full" src={product.ImageUrl} alt={product.Name} />
                  </div>
                ) : null}
                <div className="item-card-split-body">
                  <h1 className="item-title">{product.Name}</h1>
                  {product.Description ? (
                    <p className="item-text">{product.Description}</p>
                  ) : null}
                </div>
              </div>

              {excerpts.map((ex, i) => (
                <ExcerptCard key={ex.Id} excerpt={ex} reverse={i % 2 === 0} />
              ))}
            </div>
          </section>

          {prices.length > 0 ? <Pricing prices={prices} product={product} /> : null}
        </>
      )}
    </div>
  );
}

export default ProductDetailPage;
